/**
 * Companies Page (KYB)
 *
 * Main companies list page with filtering, search, and CRUD operations.
 * Migrated to Tailwind + shadcn/ui
 */

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Building2,
  Plus,
  Search,
  Filter,
  ChevronRight,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  Users,
  Globe,
  Shield,
  Upload,
  Download,
} from 'lucide-react';
import { useCompanies, useCreateCompany, useExportCompaniesCSV } from '../../hooks';
import { useTestMode } from '../../contexts/TestModeContext';
import { useDebounce } from '../../hooks/useDebounce';
import CompanyForm from './CompanyForm';
import CompanyImportModal from './CompanyImportModal';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import { Spinner } from '../ui/spinner';
// cn is available in this file if needed for conditional styling

const STATUS_CONFIG = {
  pending: { label: 'Pending', variant: 'secondary', icon: Clock },
  in_review: { label: 'In Review', variant: 'info', icon: Search },
  approved: { label: 'Approved', variant: 'success', icon: CheckCircle },
  rejected: { label: 'Rejected', variant: 'destructive', icon: XCircle },
};

const RISK_CONFIG = {
  low: { label: 'Low', variant: 'success' },
  medium: { label: 'Medium', variant: 'warning' },
  high: { label: 'High', variant: 'destructive' },
};

export default function CompaniesPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [riskFilter, setRiskFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  const debouncedSearch = useDebounce(search, 300);
  const { isTestMode } = useTestMode();

  const filters = useMemo(() => ({
    ...(debouncedSearch && { search: debouncedSearch }),
    ...(statusFilter && { status: statusFilter }),
    ...(riskFilter && { risk_level: riskFilter }),
    is_sandbox: isTestMode,
    limit: 50,
  }), [debouncedSearch, statusFilter, riskFilter, isTestMode]);

  const { data, isLoading, error } = useCompanies(filters);
  const createMutation = useCreateCompany();
  const exportCSVMutation = useExportCompaniesCSV();

  const handleCreate = async (formData) => {
    try {
      const company = await createMutation.mutateAsync(formData);
      setShowCreateModal(false);
      navigate(`/companies/${company.id}`);
    } catch (err) {
      console.error('Failed to create company:', err);
    }
  };

  const companies = data?.items || [];

  return (
    <div className="max-w-[1400px]">
      {/* Page Header */}
      <div className="flex justify-between items-start mb-6 gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <Building2 className="w-8 h-8 text-teal-500" />
          <div>
            <h1 className="text-2xl font-semibold text-grey-900 dark:text-grey-50 m-0">
              Companies
            </h1>
            <p className="text-sm text-grey-500 dark:text-grey-400 mt-1">
              Know Your Business (KYB) verification and management
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => exportCSVMutation.mutate({ is_sandbox: isTestMode })}
            disabled={exportCSVMutation.isPending}
          >
            <Download size={16} />
            {exportCSVMutation.isPending ? 'Exporting...' : 'Export CSV'}
          </Button>
          <Button variant="secondary" onClick={() => setShowImportModal(true)}>
            <Upload size={16} />
            Import
          </Button>
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus size={16} />
            Add Company
          </Button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-[400px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-grey-400" />
          <Input
            type="text"
            placeholder="Search companies..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button
          variant={showFilters ? 'default' : 'secondary'}
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter size={16} />
          Filters
        </Button>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="flex gap-3 p-4 mb-6 flex-wrap">
          <select
            className="px-3 py-2 bg-grey-50 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 rounded-md text-sm text-grey-900 dark:text-grey-50 min-w-[150px] cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="in_review">In Review</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
          <select
            className="px-3 py-2 bg-grey-50 dark:bg-grey-800 border border-grey-200 dark:border-grey-700 rounded-md text-sm text-grey-900 dark:text-grey-50 min-w-[150px] cursor-pointer focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500"
            value={riskFilter}
            onChange={(e) => setRiskFilter(e.target.value)}
          >
            <option value="">All Risk Levels</option>
            <option value="low">Low Risk</option>
            <option value="medium">Medium Risk</option>
            <option value="high">High Risk</option>
          </select>
          {(statusFilter || riskFilter) && (
            <button
              className="px-3 py-2 bg-transparent border-none text-teal-500 text-sm cursor-pointer hover:text-teal-600"
              onClick={() => {
                setStatusFilter('');
                setRiskFilter('');
              }}
            >
              Clear Filters
            </button>
          )}
        </Card>
      )}

      {/* Companies Table */}
      <Card className="overflow-hidden">
        {/* Table Header */}
        <div className="hidden md:grid grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_80px_40px] gap-4 px-5 py-3 bg-grey-50 dark:bg-grey-800/50 border-b border-grey-200 dark:border-grey-700">
          <div className="text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider">Company</div>
          <div className="text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider">Registration</div>
          <div className="text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider">Country</div>
          <div className="text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider">Status</div>
          <div className="text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider">Risk</div>
          <div className="text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider">Screening</div>
          <div className="text-xs font-semibold text-grey-500 dark:text-grey-400 uppercase tracking-wider">UBOs</div>
          <div></div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex flex-col items-center justify-center py-16 text-grey-400">
            <Spinner size="lg" />
            <div className="mt-3 text-sm">Loading companies...</div>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="flex flex-col items-center justify-center py-16 text-center text-red-500">
            <AlertTriangle size={32} />
            <div className="mt-3 text-sm">Failed to load companies</div>
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && companies.length === 0 && (
          <div className="py-16 text-center">
            <Building2 className="w-12 h-12 text-grey-300 dark:text-grey-600 mx-auto mb-4" />
            <h3 className="text-base font-medium text-grey-900 dark:text-grey-50 mb-2">
              No companies found
            </h3>
            <p className="text-sm text-grey-500 dark:text-grey-400">
              {search || statusFilter || riskFilter
                ? 'Try adjusting your filters'
                : 'Add your first company to get started'}
            </p>
          </div>
        )}

        {/* Company Rows */}
        {!isLoading && !error && companies.map((company) => {
          const status = STATUS_CONFIG[company.status] || STATUS_CONFIG.pending;
          const StatusIcon = status.icon;
          const risk = RISK_CONFIG[company.risk_level];

          return (
            <div
              key={company.id}
              className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr_1fr_1fr_80px_40px] gap-4 px-5 py-4 border-b border-grey-200 dark:border-grey-700 last:border-b-0 cursor-pointer transition-colors hover:bg-grey-50 dark:hover:bg-grey-800/50 items-center"
              onClick={() => navigate(`/companies/${company.id}`)}
            >
              {/* Company Name + Flags */}
              <div className="flex flex-col gap-1">
                <span className="text-sm font-medium text-grey-900 dark:text-grey-50">
                  {company.legal_name}
                </span>
                {company.trading_name && company.trading_name !== company.legal_name && (
                  <span className="text-xs text-grey-500 dark:text-grey-400">
                    DBA: {company.trading_name}
                  </span>
                )}
                {company.flags?.length > 0 && (
                  <div className="flex gap-1 mt-0.5">
                    {company.flags.map((flag) => (
                      <span key={flag} className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded" style={{ background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444' }}>
                        {flag.replace(/_/g, ' ')}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Registration Number */}
              <div className="text-sm text-grey-500 dark:text-grey-400 font-mono">
                {company.registration_number || '—'}
              </div>

              {/* Country */}
              <div>
                {company.incorporation_country ? (
                  <span className="inline-flex items-center gap-1.5 px-2 py-1 bg-grey-100 dark:bg-grey-800 rounded text-xs text-grey-600 dark:text-grey-400">
                    <Globe size={12} />
                    {company.incorporation_country}
                  </span>
                ) : (
                  <span className="text-grey-400">—</span>
                )}
              </div>

              {/* Status */}
              <div>
                <Badge variant={status.variant} className="inline-flex items-center gap-1">
                  <StatusIcon size={12} />
                  {status.label}
                </Badge>
              </div>

              {/* Risk */}
              <div>
                {risk ? (
                  <Badge variant={risk.variant} className="inline-flex items-center gap-1">
                    <Shield size={12} />
                    {risk.label}
                  </Badge>
                ) : (
                  <span className="text-grey-400">—</span>
                )}
              </div>

              {/* Screening Status */}
              <div>
                {company.screening_status === 'clear' ? (
                  <span className="inline-flex items-center gap-1 text-xs text-green-500">
                    <CheckCircle size={14} />
                    Clear
                  </span>
                ) : company.screening_status === 'hits_pending' ? (
                  <span className="inline-flex items-center gap-1 text-xs text-orange-500">
                    <AlertTriangle size={14} />
                    Hits
                  </span>
                ) : company.screening_status === 'error' ? (
                  <span className="inline-flex items-center gap-1 text-xs text-red-500">
                    <XCircle size={14} />
                    Error
                  </span>
                ) : (
                  <span className="text-grey-400 text-xs">—</span>
                )}
              </div>

              {/* UBO Count */}
              <div className="flex items-center gap-1.5 text-sm text-grey-500 dark:text-grey-400">
                <Users size={14} />
                {company.ubo_count || 0}
              </div>

              {/* Chevron */}
              <div className="text-grey-400 hidden md:block">
                <ChevronRight size={16} />
              </div>
            </div>
          );
        })}
      </Card>

      {/* Create Modal */}
      {showCreateModal && (
        <CompanyForm
          onSubmit={handleCreate}
          onClose={() => setShowCreateModal(false)}
          isSubmitting={createMutation.isPending}
        />
      )}

      {/* Import Modal */}
      {showImportModal && (
        <CompanyImportModal
          onClose={() => setShowImportModal(false)}
          onImported={() => setShowImportModal(false)}
        />
      )}
    </div>
  );
}
